import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { useCustomEventListener } from "react-custom-events";
import { DefaultLinkModel } from "@projectstorm/react-diagrams";
import Canvas from "./Canvas";
import SelectNEDrawer from "./Canvas/SelectNEDrawer";
import AddPortService from "../../services/addPort.service";
import DashboardService from "../../services/dashboard.service";
import CrossConnectService from "../../services/crossConnect.service";
import { addNEType, editNEPayload, ZOOM_CONSTANTS } from "../../utils/appConstants";
import SuccessInfoIcon from "../../assets/images/success-toast-checkmark-icon.svg";
import ErrorInfoIcon from "../../assets/images/error-info-icon.svg";
import { ErrorCodeMessageMapping, ErrorCodesMapping, SuccessMessage, ErrorMessages as Errors } from "../../utils/apiConstants";
import { getPortIdFromName } from "../../utils/methods";
import { useIsMounted } from "../../utils/customHooks";

const { DefaultScale } = ZOOM_CONSTANTS;

const Dashboard: React.FC = () => {
  const [selectedNes, setSelectedNes] = useState<any[]>([]);
  const [scale, setScale] = useState<any>(null);
  const [selectNEOpen, setSelectNEOpen] = useState(false);
  const [portsList, setPortsList] = useState<any>(null);
  const [canvasData, setCanvasData] = useState<any>(null);
  const [portsLoading, setPortsLoading] = useState<boolean>(false);
  const [networkElements, setNetworkElements] = useState<any[]>([]);
  const [expandedGroupIds, setExpandedGroupIds] = useState<any[]>([]);
  // const [dirtyData, setDirtyData] = useState<{
  //   create: any[];
  //   update: any[];
  //   delete: any[];
  // }>({
  //   create: [],
  //   update: [],
  //   delete: [],
  // });
  const [crossConnectedPortIdsDB, setCrossConnectedPortIdsDB] = useState<any[]>([]);

  const isMounted = useIsMounted();

  const handleExpandedGroupIdsUpdate = useCallback(
    (groupIds: any[]) => {
      setExpandedGroupIds(groupIds);
    },
    [setExpandedGroupIds]
  );

  const updateCrossConnectedPortIdsDB = useCallback(
    (values: any[]) => {
      setCrossConnectedPortIdsDB(values);
    },
    [setCrossConnectedPortIdsDB]
  );

  const isPortInBandwidth = (portId: string) => {
    let isInBandwidth = false;
    if (portsList && portsList.nodes) {
      portsList.nodes.forEach((node: any) => {
        if (!isInBandwidth) {
          const bandWidthPorts = node.bandwidth_ports_used;
          if (bandWidthPorts) {
            const list = bandWidthPorts.find((p: any) => p.portId == portId);
            if (list) {
              isInBandwidth = true;
            }
          }
          const bandWidthGrpPorts = node.bandwidth_groups_used;
          if (bandWidthGrpPorts) {
            bandWidthGrpPorts.forEach((item: any) => {
              const list = item.ports.find((p: any) => p.portId == portId);
              if (list) {
                isInBandwidth = true;
              }
            });
          }
        }
      });
    }
    return isInBandwidth;
  };

  const formatDirtyDataForCreate = (newData: any) => {
    const linkedCreated: any = [];
    if (newData && newData.create) {
      newData.create.forEach((element: any) => {
        const link1 = element.data as DefaultLinkModel;
        const sourcePort = link1.getSourcePort();
        const targetPort = link1.getTargetPort();
        if (sourcePort && targetPort) {
          const sourcePortId = getPortIdFromName(sourcePort.getOptions().name);
          const targetPortId = getPortIdFromName(targetPort.getOptions().name);
          const bandWidth = {
            source_port_id: sourcePortId,
            source_bandwidth_port: isPortInBandwidth(sourcePortId),
            destination_port_id: targetPortId,
            destination_bandwidth_port: isPortInBandwidth(targetPortId),
          };
          linkedCreated.push(bandWidth);
        }
      });
    }
    return linkedCreated;
  };

  const formatDirtyDataForDelete = (newData: any) => {
    const linkedDeleted: any = [];
    if (newData && newData.delete) {
      newData.delete.forEach((element: any) => {
        const link1 = element.data as DefaultLinkModel;
        const sourcePort = link1.getSourcePort();
        const targetPort = link1.getTargetPort();
        if (sourcePort && targetPort) {
          const sourcePortId = getPortIdFromName(sourcePort.getOptions().name);
          const targetPortId = getPortIdFromName(targetPort.getOptions().name);
          const bandWidth = {
            source_port_id: sourcePortId,
            source_bandwidth_port: isPortInBandwidth(sourcePortId),
            destination_port_id: targetPortId,
            destination_bandwidth_port: isPortInBandwidth(targetPortId),
          };
          linkedDeleted.push(bandWidth);
        }
      });
    }
    return linkedDeleted;
  };

  const formatDirtyDataForSave = (newData: any) => {
    const linksCreated = formatDirtyDataForCreate(newData);
    const linksDeleted = formatDirtyDataForDelete(newData);
    const finalData = {
      connect: linksCreated,
      disconnect: linksDeleted,
    };
    return finalData;
  };

  const fetchPorts = useCallback(
    (nodeIdsToFetch: number[]) => {
      setPortsLoading(true);

      const payload = {
        nodeIds: nodeIdsToFetch,
      };

      DashboardService.getNodeWiseNr(payload)
        .then((response: any) => {
          if (!isMounted) return;
          const portsDetail = {
            nodes: response.data.neResultList || [],
            bandwidths: response.data.bdResultList || [],
            cross_connects: response.data.ccResultList || [],
          };
          setPortsList(portsDetail);
          setPortsLoading(false);
        })
        .catch(() => {
          if (!isMounted) return;
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
          setPortsLoading(false);
          setPortsList([]);
        });
    },
    [setPortsList, setPortsLoading]
  );

  const fetchCanvasData = useCallback(() => {
    DashboardService.getCanvasData()
      .then((response) => {
        if (!isMounted) return;
        if (response.data.canvasPosition) {
          setCanvasData(response.data.canvasPosition);
          setScale(response.data.canvasPosition.zoom);
        } else {
          setScale(DefaultScale);
        }
      })
      .catch((e) => {
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
      });
  }, []);

  const fetchNodeIds = useCallback(() => {
    setPortsLoading(true);
    // fetchCanvasData();
    DashboardService.getSelectedNodes()
      .then((response: any) => {
        if (!isMounted) return;
        setSelectedNes(response.data.selected_node_ids || []);
        fetchPorts(response.data.selected_node_ids || []);
        setPortsLoading(false);
      })
      .catch(() => {
        if (!isMounted) return;
        toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" className="error-icon" /> });
        setSelectedNes([]);
        setPortsLoading(false);
      });
  }, [setSelectedNes, setPortsLoading]);

  const handleDeleteClick = useCallback(
    (selectedNetworkElement: any) => {
      const newSelected = selectedNes.filter((item: number) => item != selectedNetworkElement.id);
      setSelectedNes(newSelected);
    },
    [setSelectedNes, selectedNes]
  );

  useCustomEventListener("canvas-isdiry", (data: any) => {
    const isDirty = data.isdirty;
  });

  const handleNeClose = useCallback(
    (updatedNodeIds: any[] | null = null) => {
      setSelectNEOpen(false);
      if (updatedNodeIds) setSelectedNes(updatedNodeIds);
    },
    [setSelectNEOpen, setSelectedNes]
  );

  const handleNeOpen = useCallback(() => {
    setSelectNEOpen(true);
  }, [setSelectNEOpen]);

  const [loadingNE, setLoadingNE] = useState(false);
  const fetchNetworkElements = useCallback(() => {
    setLoadingNE(true);
    AddPortService.getNetworkElements()
      .then((response: any) => {
        if (!isMounted) return;
        setNetworkElements(response.data || []);
      })
      .catch(() => {
        if (!isMounted) return;
        setNetworkElements([]);
      });
    setLoadingNE(false);
  }, [setNetworkElements, setLoadingNE]);

  const refetch = useCallback(() => {
    fetchNetworkElements();
    fetchCanvasData();
    fetchNodeIds();
  }, [fetchNetworkElements, fetchCanvasData, fetchNodeIds]);

  useEffect(() => {
    refetch();
  }, [fetchNodeIds]);

  const firstUpdate = useRef(true);
  useLayoutEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    async function fetchDataAPI() {
      fetchCanvasData();
      fetchPorts(selectedNes);
    }
    fetchDataAPI();
  }, [selectedNes]);
  /*
    WARNING!!!!!!!!!!!
    Be careful using data canvasData on newly added elements. 
    CanvasData is often null upon inital element addition to canvas,
    and thus can crash the site if called upon without any error checks. 
  */
  const handleAddNetworkElement = useCallback(
    (data: addNEType) => {
      DashboardService.addNetworkElement(data)
        .then((response: any) => {
          toast.success(SuccessMessage.neAddedSuccess, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          fetchNetworkElements();
          handleNeClose();
        })
        .catch((e: any) => {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" /> });
        });
    },
    [fetchNetworkElements]
  );

  const handleEditNetworkElement = useCallback(
    (data: editNEPayload) => {
      DashboardService.editNetworkElement(data)
        .then((response: any) => {
          if (!isMounted) return;
          toast.success(SuccessMessage.neEditedSuccess, { icon: <img src={SuccessInfoIcon} alt="Success" /> });
          fetchNetworkElements();
          handleNeClose();
        })
        .catch((e: any) => {
          toast.error(Errors.internalServerError, { icon: <img src={ErrorInfoIcon} alt="Error" /> });
        });
    },
    [fetchNetworkElements]
  );

  const handleRefetchNodes = useCallback(() => {
    fetchNodeIds();
  }, [fetchNodeIds]);

  const selectedNetworkElements = React.useMemo(() => {
    return networkElements.filter((ele) => selectedNes.includes(ele.id));
  }, [selectedNes, networkElements]);

  return (
    <>
      <Canvas
        refetch={refetch}
        isMounted={isMounted}
        selectedNetworkElements={selectedNetworkElements}
        scale={scale}
        ports={portsList}
        canvasData={canvasData}
        portsLoading={portsLoading}
        onRefetch={handleRefetchNodes}
        onNeOpen={handleNeOpen}
        onDeleteClick={handleDeleteClick}
        // dirtyData={dirtyData}
        // setDirtyData={setDirtyDataFromCanvas}
        crossConnectedPortIdsDB={crossConnectedPortIdsDB}
        setCrossConnectedPortIdsDB={updateCrossConnectedPortIdsDB}
        parentExpandedGroupIds={expandedGroupIds}
        setParentExpandedGroupIds={handleExpandedGroupIdsUpdate}
        // menuProps={menuProps}
        setCanvasData={setCanvasData}
        // handleSaveReset={handleSaveReset}
      />
      <SelectNEDrawer
        selectedNes={selectedNes}
        onNeOpen={handleNeOpen}
        networkElements={networkElements}
        onNeClose={handleNeClose}
        open={selectNEOpen}
        loading={loadingNE}
        addNetworkElement={handleAddNetworkElement}
        editNetworkElement={handleEditNetworkElement}
      />
    </>
  );
};

export default Dashboard;
